/**
 * Service catalog v2 domain types (epic #220, фаза 5 — фронт).
 *
 * Контракт — из согласованного proposal
 * `lovart-hotels-service-catalog-architecture-v2-2026-06-11.html`, раздел 2:
 *   - per-tenant каталог: категории → услуги → варианты;
 *   - name/description — JSONB по локалям ({"ru": …, "en": …}) с fallback
 *     на дефолтную локаль тенанта;
 *   - price_mode: none | fixed | from | by_variant; цены в minor units +
 *     код валюты из набора валют отеля (tenant_currencies);
 *   - show_price=false — «не показывать цену» на витрине;
 *   - per_person на варианте — множитель «на человека»;
 *   - is_system — из seed, удалять нельзя (только деактивация);
 *   - workflow_templates — конструктор шаблонов процессов (per-tenant);
 *   - catalog_presets — платформенные снимки «категории+услуги+шаблоны».
 *
 * ВНИМАНИЕ: backend (фазы 1-3) идёт параллельно. Поля названы по DDL
 * proposal'а (snake_case как в остальных HTTP DTO проекта). При выходе
 * API Contracts v1.5 сверить и при расхождении поправить ЗДЕСЬ — все
 * consumers (tenant-app витрина, admin-app конструктор) типизированы
 * через этот модуль.
 */
import type { Brand } from './index.js';

/* ===================== Branded IDs ===================== */

export type ServiceCategoryId = Brand<string, 'ServiceCategoryId'>;
export type ServiceItemId = Brand<string, 'ServiceItemId'>;
export type ServiceItemVariantId = Brand<string, 'ServiceItemVariantId'>;
export type WorkflowTemplateId = Brand<string, 'WorkflowTemplateId'>;
export type CatalogPresetId = Brand<string, 'CatalogPresetId'>;

export const ServiceCategoryId = (s: string): ServiceCategoryId => s as ServiceCategoryId;
export const ServiceItemId = (s: string): ServiceItemId => s as ServiceItemId;
export const ServiceItemVariantId = (s: string): ServiceItemVariantId => s as ServiceItemVariantId;
export const WorkflowTemplateId = (s: string): WorkflowTemplateId => s as WorkflowTemplateId;
export const CatalogPresetId = (s: string): CatalogPresetId => s as CatalogPresetId;

/* ===================== i18n ===================== */

/**
 * JSONB-словарь локализованных строк: `{"ru": "Уборка", "en": "Cleaning"}`.
 * Ключ — код локали (ru/en/...), добавление третьего языка = новый ключ.
 */
export type LocalizedText = Readonly<Record<string, string>>;

/**
 * Резолв локализованной строки: запрошенная локаль → fallback-локаль →
 * первое непустое значение → пустая строка.
 */
export function localizeText(
  text: LocalizedText | undefined,
  locale: string,
  fallbackLocale = 'ru',
): string {
  if (!text) return '';
  const direct = text[locale];
  if (direct !== undefined && direct !== '') return direct;
  const fallback = text[fallbackLocale];
  if (fallback !== undefined && fallback !== '') return fallback;
  for (const v of Object.values(text)) {
    if (v !== '') return v;
  }
  return '';
}

/* ===================== Price ===================== */

export const PRICE_MODES = ['none', 'fixed', 'from', 'by_variant'] as const;
export type PriceMode = (typeof PRICE_MODES)[number];

/** Метка режима цены для админ-форм (RU). */
export const PRICE_MODE_LABELS: Readonly<Record<PriceMode, string>> = {
  none: 'Без цены',
  fixed: 'Фиксированная',
  from: 'Цена «от»',
  by_variant: 'По вариантам',
};

/**
 * NBSP/narrow-NBSP → обычный пробел: Intl для ru-RU вставляет U+00A0 и
 * U+202F между группами разрядов; нормализуем как в guest-portal
 * `formatKopecks` (frozen spec ожидает обычные пробелы).
 */
const NBSP_RE = new RegExp('[\\u00A0\\u202F]', 'g');

/**
 * Форматирует цену из minor units: 350000 + RUB → «3 500 ₽».
 * Копейки показываем только если сумма не круглая.
 */
export function formatPriceMinor(priceMinor: number, currency: string, locale = 'ru'): string {
  const major = priceMinor / 100;
  const wholeMajor = priceMinor % 100 === 0;
  try {
    return new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: wholeMajor ? 0 : 2,
      maximumFractionDigits: wholeMajor ? 0 : 2,
    })
      .format(major)
      .replace(NBSP_RE, ' ');
  } catch {
    // Неизвестный код валюты (пока набор валют отеля не загружен) — без стиля.
    return `${major} ${currency}`;
  }
}

/* ===================== Admin DTO (полная форма, JSONB-локали) ===================== */

/** service_categories (tenant-схема). */
export interface ServiceCategory {
  id: ServiceCategoryId;
  /** Слаг, format-CHECK `^[a-z][a-z0-9_]{2,30}$`. */
  code: string;
  name: LocalizedText;
  description?: LocalizedText;
  /** FK на per-tenant справочник департаментов (departments.code). */
  department_code: string;
  workflow_template_id?: WorkflowTemplateId;
  /** Опциональные ДЕФОЛТЫ SLA для услуг категории. */
  sla_response_min?: number;
  sla_resolution_min?: number;
  /** Ключ иконки категории (свободная строка; id иконки из /v1/icons). */
  icon?: string;
  /** Категория доступна для создания заявки (тип заявки, миграция 0068). */
  is_request_type: boolean;
  /** Из seed — удалять нельзя, только деактивировать. */
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface ServiceCategoryCreateInput {
  code: string;
  name: LocalizedText;
  description?: LocalizedText;
  department_code: string;
  workflow_template_id?: WorkflowTemplateId;
  sla_response_min?: number;
  sla_resolution_min?: number;
  /** Ключ иконки категории (id иконки из /v1/icons или ''). */
  icon?: string;
  /** Пометить категорию типом заявки (доступна в форме «Новая заявка»). */
  is_request_type?: boolean;
  sort_order?: number;
}

/** PATCH-набор: любое подмножество полей, включая is_active (деактивация). */
export type ServiceCategoryUpdateInput = Partial<ServiceCategoryCreateInput> & {
  is_active?: boolean;
};

/** service_items. */
export interface ServiceItem {
  id: ServiceItemId;
  category_id: ServiceCategoryId;
  code: string;
  name: LocalizedText;
  description?: LocalizedText;
  is_paid: boolean;
  price_mode: PriceMode;
  /** Для fixed / витринной «от», в minor units. */
  price_minor?: number;
  /** Код валюты из набора валют отеля (tenant_currencies). */
  currency?: string;
  /** «Выводить цену или нет» на витрине. */
  show_price: boolean;
  sla_response_min?: number;
  sla_resolution_min?: number;
  workflow_template_id?: WorkflowTemplateId;
  /** Исполнитель услуги: request (заявка) | external_link (ссылка вендора). */
  executor?: ServiceExecutor;
  /** Ссылка вендора (только при executor=external_link). */
  external_url?: string;
  /** Фото позиции (MinIO-ключ или http(s)-URL); опционально. */
  image_url?: string;
  is_active: boolean;
  sort_order: number;
  /** Варианты (admin-ответ отдаёт вложенно; на витрине — CatalogViewVariant). */
  variants?: ServiceItemVariant[];
}

export interface ServiceItemCreateInput {
  category_id: ServiceCategoryId;
  code: string;
  name: LocalizedText;
  description?: LocalizedText;
  is_paid?: boolean;
  price_mode?: PriceMode;
  price_minor?: number;
  currency?: string;
  show_price?: boolean;
  sla_response_min?: number;
  sla_resolution_min?: number;
  workflow_template_id?: WorkflowTemplateId;
  executor?: ServiceExecutor;
  external_url?: string;
  /** Фото позиции (MinIO-ключ или http(s)-URL); опционально. */
  image_url?: string;
  sort_order?: number;
}

export type ServiceItemUpdateInput = Partial<Omit<ServiceItemCreateInput, 'category_id'>> & {
  is_active?: boolean;
};

/** service_item_variants — «тип машины», позиция меню, экскурсия. */
export interface ServiceItemVariant {
  id: ServiceItemVariantId;
  item_id: ServiceItemId;
  name: LocalizedText;
  price_minor: number;
  /** По умолчанию наследует валюту услуги. */
  currency?: string;
  /** Множитель «на человека». */
  per_person: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface ServiceItemVariantCreateInput {
  item_id: ServiceItemId;
  name: LocalizedText;
  price_minor: number;
  currency?: string;
  per_person?: boolean;
  sort_order?: number;
}

export type ServiceItemVariantUpdateInput = Partial<
  Omit<ServiceItemVariantCreateInput, 'item_id'>
> & { is_active?: boolean };

/* ===================== Workflow templates (конструктор) ===================== */

/** Исполнитель шага шаблона: департамент | vendor-пользователь | авто. */
export const WORKFLOW_EXECUTOR_KINDS = ['department', 'vendor', 'auto'] as const;
export type WorkflowExecutorKind = (typeof WORKFLOW_EXECUTOR_KINDS)[number];

/** Шаг шаблона процесса (steps JSONB). Минимальная типизация по proposal 2.5. */
export interface WorkflowStep {
  executor: WorkflowExecutorKind;
  /** Код департамента / id vendor-пользователя — в зависимости от executor. */
  executor_ref?: string;
  action: string;
  timeout_min?: number;
  escalation_min?: number;
  notify?: readonly string[];
}

export interface WorkflowTemplate {
  id: WorkflowTemplateId;
  code: string;
  name: LocalizedText;
  steps: WorkflowStep[];
  is_system: boolean;
  is_active: boolean;
}

export interface WorkflowTemplateCreateInput {
  code: string;
  name: LocalizedText;
  steps: WorkflowStep[];
}

export type WorkflowTemplateUpdateInput = Partial<WorkflowTemplateCreateInput> & {
  is_active?: boolean;
};

/* ===================== Presets ===================== */

/** Платформенный снимок «категории + услуги + шаблоны» (catalog_presets). */
export interface CatalogPreset {
  id: CatalogPresetId;
  code: string;
  name: LocalizedText;
  description?: LocalizedText;
  /** Сколько категорий/услуг внутри снимка (для списка в админке). */
  categories_count?: number;
  items_count?: number;
  created_at?: string;
}

/** «Сохранить текущий каталог как набор» — кнопка в админке. */
export interface CatalogPresetSaveInput {
  code: string;
  name: LocalizedText;
  description?: LocalizedText;
}

/* ===================== List envelopes (admin) ===================== */

export interface ServiceCategoriesListResponse {
  items: ServiceCategory[];
  count: number;
}

export interface ServiceItemsListResponse {
  items: ServiceItem[];
  count: number;
}

export interface ServiceItemVariantsListResponse {
  items: ServiceItemVariant[];
  count: number;
}

export interface WorkflowTemplatesListResponse {
  items: WorkflowTemplate[];
  count: number;
}

export interface CatalogPresetsListResponse {
  items: CatalogPreset[];
  count: number;
}

/* ===================== Витрина гостя (GET /v1/service-catalog?locale=) ===================== */

/**
 * Витринный ответ уже локализован бэкендом по `?locale=` (строки, не JSONB):
 * локаль гостя → локаль из JSONB → fallback на tenants.default_locale.
 * Неактивные категории/услуги/варианты на витрину не попадают.
 */
export interface CatalogViewVariant {
  id: ServiceItemVariantId;
  name: string;
  price_minor: number;
  currency: string;
  per_person: boolean;
}

export interface CatalogViewItem {
  id: ServiceItemId;
  code: string;
  name: string;
  description?: string;
  is_paid: boolean;
  price_mode: PriceMode;
  /**
   * Резолвнутая витринная цена позиции (бэк отдаёт вложенным объектом
   * `price`: { amount_minor, currency, from }). Отсутствует для none/
   * by_variant и когда show_price=false. ЕДИНСТВЕННЫЙ источник цены позиции
   * на витрине — раньше читали несуществующие плоские price_minor/currency,
   * из-за чего фикс-цены не показывались («нет цен на меню»).
   */
  price?: { amount_minor: number; currency: string; from: boolean };
  /** @deprecated бэк не отдаёт плоское поле; цена приходит в `price`. */
  price_minor?: number;
  /** @deprecated бэк не отдаёт плоское поле; валюта приходит в `price`. */
  currency?: string;
  show_price: boolean;
  /**
   * Как обслуживается тап по услуге («Облака» #31):
   *   - `request` (по умолчанию) — открывает флоу заявки;
   *   - `external_link` — открывает `external_url` в новой вкладке, заявку НЕ создаёт.
   * Поле может отсутствовать у старых ответов — трактуем как `request`.
   */
  executor?: ServiceExecutor;
  /** Ссылка вендора для услуг-внешних-ссылок (трансфер / экскурсии). */
  external_url?: string;
  /** Фото позиции (MinIO-ключ или http(s)-URL) для гостевого меню; опционально. */
  image_url?: string;
  /**
   * Теги аллергенов/диет (lactose, nuts, gluten, veg, …) для гостевого меню
   * (design-guest v7 §11). Бэк всегда отдаёт массив (может быть пустой);
   * поле опционально для совместимости со старыми ответами.
   */
  allergens?: string[];
  variants: CatalogViewVariant[];
}

/** Тип исполнителя услуги: заявка или внешняя ссылка. */
export const SERVICE_EXECUTORS = ['request', 'external_link'] as const;
export type ServiceExecutor = (typeof SERVICE_EXECUTORS)[number];

/** Услуга-внешняя-ссылка: тап открывает URL вендора, заявку не создаём. */
export function isExternalLinkItem(item: CatalogViewItem): item is CatalogViewItem & {
  external_url: string;
} {
  return (
    item.executor === 'external_link' &&
    typeof item.external_url === 'string' &&
    item.external_url.length > 0
  );
}

export interface CatalogViewCategory {
  id: ServiceCategoryId;
  code: string;
  name: string;
  description?: string;
  items: CatalogViewItem[];
}

export interface CatalogViewResponse {
  locale: string;
  categories: CatalogViewCategory[];
}

/**
 * Витринная строка цены услуги по price_mode / show_price:
 *   - show_price=false или mode=none → null (цену не показываем);
 *   - fixed → «3 500 ₽»;
 *   - from  → «от 3 500 ₽»;
 *   - by_variant → «от <минимальная цена варианта>» (+ «/чел.» если
 *     минимальный вариант per_person);
 * Бесплатная услуга (is_paid=false, mode=none) — тоже null: цену не пишем.
 */
export function displayItemPrice(item: CatalogViewItem, locale = 'ru'): string | null {
  if (!item.show_price || item.price_mode === 'none') return null;

  if (item.price_mode === 'by_variant') {
    const active = item.variants;
    if (active.length === 0) return null;
    let min = active[0];
    for (const v of active) {
      if (min === undefined || v.price_minor < min.price_minor) min = v;
    }
    if (min === undefined) return null;
    const base = `от ${formatPriceMinor(min.price_minor, min.currency, locale)}`;
    return min.per_person ? `${base} / чел.` : base;
  }

  // Цена приходит вложенным объектом `price` (бэк guestItemView). Плоские
  // price_minor/currency бэк не отдаёт — оставлены deprecated для совместимости.
  const minor = catalogItemPriceMinor(item);
  const currency = catalogItemCurrency(item);
  if (minor === undefined || currency === undefined) return null;
  const formatted = formatPriceMinor(minor, currency, locale);
  return item.price_mode === 'from' ? `от ${formatted}` : formatted;
}

/** Цена позиции в минорных единицах (из `price`, fallback на плоское поле). */
export function catalogItemPriceMinor(item: CatalogViewItem): number | undefined {
  return item.price?.amount_minor ?? item.price_minor;
}

/** Валюта цены позиции (из `price`, fallback на плоское поле). */
export function catalogItemCurrency(item: CatalogViewItem): string | undefined {
  return item.price?.currency ?? item.currency;
}

/** Витринная строка цены варианта: «1 200 ₽» / «1 200 ₽ / чел.». */
export function displayVariantPrice(v: CatalogViewVariant, locale = 'ru'): string {
  const base = formatPriceMinor(v.price_minor, v.currency, locale);
  return v.per_person ? `${base} / чел.` : base;
}

/* ===================== Заявка на услугу (v2) ===================== */

/**
 * POST /v1/requests — формат фазы 2 (service_requests: category_id UUID FK,
 * nullable item_id/variant_id; заявка может быть и «свободной» в категории,
 * и на конкретную услугу/вариант). title/description — как в текущем
 * request HTTP DTO. Go-код здесь не трогаем — только типы.
 */
export interface ServiceRequestCreateInput {
  category_id: ServiceCategoryId;
  item_id?: ServiceItemId;
  variant_id?: ServiceItemVariantId;
  title: string;
  description?: string;
  stay_id?: string;
  priority?: string;
  /**
   * Объектные ключи фото (до 3), загруженных через POST /v1/files/presign
   * + PUT в MinIO. Canon design-guest §16: common-поле для всех форм.
   * Backend фазы 2 должен принять это поле — сверить с API Contracts v1.5.
   */
  photos?: readonly string[];
  /**
   * Структурный список позиций заказа (корзина меню §12, #74). Один сабмит
   * корзины = ОДНА заявка со всеми позициями. Пусто/нет — обычная заявка.
   */
  items?: readonly ServiceRequestItemInput[];
}

/** Одна позиция заказа в POST /v1/requests.items (#74). */
export interface ServiceRequestItemInput {
  item_id?: ServiceItemId;
  category_id?: ServiceCategoryId;
  /** Название позиции (снимок на момент заказа). */
  name: string;
  /** Вариант/размер (опц.). */
  variant?: string;
  /** Количество (>=1). */
  qty: number;
  /** Цена за единицу в минорных единицах (опц., если показывается). */
  unit_price_minor?: number;
  /** Валюта unit_price_minor (опц.). */
  currency?: string;
}

export interface ServiceRequestCreatedResponse {
  id: string;
  category_id: string;
  item_id?: string;
  variant_id?: string;
  title: string;
  state: string;
  created_at: string;
}

/* ===================== Типы заявок (0068) ===================== */

/**
 * Тип заявки — активная категория услуг с is_request_type=true. Лёгкая
 * локализованная проекция для выпадашки формы «Новая заявка»
 * (GET /v1/service-catalog/request-types).
 */
export interface RequestTypeView {
  id: ServiceCategoryId;
  code: string;
  /** Локализованное имя (уже строка, не LocalizedText). */
  name: string;
  department_code: string;
  icon?: string;
  sort_order: number;
}

export interface RequestTypesListResponse {
  request_types: RequestTypeView[];
  total: number;
}
